import {
  DerivedEfficiency,
  clampIndex,
  derivedEfficiency as deriveEfficiency,
  safeRatio
} from '../curveSliderMath';
import {
  BudgetBuilderGroup,
  BudgetOutcome,
  CurvePoint,
  CurveSeries,
  EfficiencyMetric
} from './budgetBuilderContract';

// Range / snapping / ratio math is shared with the Scaling page (see
// ../curveSliderMath); the runtime helpers are re-exported here so existing
// budget-builder importers and tests keep a single entry point. DerivedEfficiency
// is a type (used only for the wrapper's return annotation below), so it is not
// re-exported as a runtime binding — consumers that need it import it from
// ../curveSliderMath directly.
export { clampIndex, safeRatio };

/**
 * Efficiency at a CurvePoint, derived from spend & volume (the single source of
 * truth). Thin adapter over the shared point-agnostic helper.
 */
export const derivedEfficiency = (metric: EfficiencyMetric, p: CurvePoint): DerivedEfficiency =>
  deriveEfficiency(metric, p.spend, p.volume, p.volumeLower, p.volumeUpper);

/**
 * Stopgap spend-domain band, as multiples of the primary budget.
 *
 * The satcurve grid from budget_builder.R runs from a near-zero floor up to
 * ~2.2× the primary budget, so its low-spend tail sits far below where the
 * client ever spent. On a diminishing-returns power curve (ROAS = a·spend^(b−1),
 * b < 1) that tail explodes to implausible ROAS (e.g. 79× at 3.6% of primary).
 * Until the emitter anchors the grid to OBSERVED spend, we restrict the curve to
 * a band around the primary budget (which sits inside the client's real
 * operating range): a cut-to-a-quarter downside through a double-the-budget
 * upside. Replace with the observed spend floor/ceiling once the emitter sends it.
 */
export const PRIMARY_DOMAIN_LOW = 0.25;
export const PRIMARY_DOMAIN_HIGH = 2;

/**
 * Restrict a curve to [LOW, HIGH] × primary. No-op when there's no primary
 * budget or when the band would leave fewer than two points (so the chart and
 * sliders always have a range to work with).
 */
export const clampCurveToPrimary = (curve: CurvePoint[], primarySpend?: number): CurvePoint[] => {
  if (primarySpend == null || primarySpend <= 0) {
    return curve;
  }
  const lo = primarySpend * PRIMARY_DOMAIN_LOW;
  const hi = primarySpend * PRIMARY_DOMAIN_HIGH;
  const clamped = curve.filter(p => p.spend >= lo && p.spend <= hi);
  return clamped.length >= 2 ? clamped : curve;
};

/**
 * The series for the page-selected outcome, falling back to the first available,
 * with its curve clamped to a realistic spend band (see clampCurveToPrimary).
 * All consumers (card, chart, table, export) go through here, so the clamped
 * curve — and therefore point indices — stay consistent across the page.
 */
export const pickSeries = (group: BudgetBuilderGroup, outcome: BudgetOutcome): CurveSeries => {
  const series = group.series.find(s => s.outcome === outcome) ?? group.series[0];
  if (!series) {
    return series;
  }
  return { ...series, curve: clampCurveToPrimary(series.curve, group.primarySpend) };
};

/**
 * Marginal outcome per dollar at index `i` — the curve's slope, via a centered
 * finite difference. For revenue this is marginal ROAS ("what the next $1
 * returns"); for volume it's incremental units per dollar.
 */
export const marginalPerDollar = (curve: CurvePoint[], i: number): number | null => {
  const a = curve[Math.max(0, i - 1)];
  const b = curve[Math.min(curve.length - 1, i + 1)];
  if (a.volume == null || b.volume == null) {
    return null;
  }
  const dSpend = b.spend - a.spend;
  return dSpend > 0 ? (b.volume - a.volume) / dSpend : null;
};

/**
 * Marginal efficiency at index `i`, in the units of the group's efficiency metric.
 * ROAS: the slope itself (Δrevenue/Δspend, "what the next $1 returns").
 * CPA: its reciprocal (Δspend/Δconversions, "what the next conversion costs"). The
 * slope alone is conversions per dollar, so 18 extra conversions per extra $1,000
 * is a marginal CPA of $55.56, not $0.02. CPA is `null` where the curve is flat or
 * falling, since no amount of extra spend buys the next conversion there.
 */
export const marginalEfficiency = (
  metric: EfficiencyMetric,
  curve: CurvePoint[],
  i: number
): number | null => {
  const perDollar = marginalPerDollar(curve, i);
  return metric === 'ROAS' ? perDollar : safeRatio(1, perDollar);
};

/**
 * Break-even spend: where marginal ROAS (Δrevenue/Δspend) falls through 1 — the
 * point past which the next dollar returns less than a dollar. `null` when the
 * curve never crosses 1 within its modeled range (e.g. a channel that stays
 * profitable at the margin across the whole CP budget range).
 */
export const breakevenSpend = (curve: CurvePoint[]): number | null => {
  for (let i = 1; i < curve.length; i += 1) {
    const prev = marginalPerDollar(curve, i - 1);
    const curr = marginalPerDollar(curve, i);
    if (prev != null && curr != null && prev >= 1 && curr < 1) {
      return curve[i].spend;
    }
  }
  return null;
};

/** Default active point: the middle of the curve. */
export const defaultIndex = (length: number): number => Math.floor((length - 1) / 2);

/**
 * A group's active curve index: the stored selection or the default, clamped to
 * the curve. Single source of truth shared by the page, table, and CSV export.
 */
export const resolveIndex = (
  selection: Record<string, number>,
  groupId: string,
  curveLength: number
): number => clampIndex(selection[groupId] ?? defaultIndex(curveLength), curveLength - 1);

export interface Scenario {
  label: string;
  index: number;
}

/**
 * Comparison scenarios for the table: lowest spend, the user's selected budget,
 * and highest spend — merged when they coincide (e.g. "Low · Selected").
 */
export const scenarioRows = (length: number, selectedIndex: number): Scenario[] => {
  const last = length - 1;
  const raw: Scenario[] = [
    { label: 'Low', index: 0 },
    { label: 'Selected', index: clampIndex(selectedIndex, last) },
    { label: 'High', index: last }
  ];
  const byIndex = new Map<number, string[]>();
  raw.forEach(r => byIndex.set(r.index, [...(byIndex.get(r.index) ?? []), r.label]));
  return [...byIndex.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([index, labels]) => ({ label: labels.join(' · '), index }));
};

/** Even-stride sample of a curve's indices, capped at `maxPoints` (keeps endpoints). */
export const sampleIndices = (length: number, maxPoints: number): number[] => {
  if (length <= maxPoints) {
    return Array.from({ length }, (_, i) => i);
  }
  const step = (length - 1) / (maxPoints - 1);
  return Array.from({ length: maxPoints }, (_, i) => Math.round(i * step));
};
